import {
  Box,
  Checkbox,
  Flex,
  Icon,
  Spinner,
  Text,
  Tooltip,
  useToast
} from '@chakra-ui/react';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { EmbeddedCheckoutProvider } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import EmbeddedCheckoutWrapper from "components/pricing/EmbeddedCheckoutWrapper";
import SidebarWithHeader from 'components/SidebarWithHeader';
import OopsPage from './OopsPage';

import { TiArrowBack } from 'react-icons/ti';
import {
  useAddPaymentMethodMutation,
  useCreateCheckoutMutation,
  useGetSubscriptionByIdQuery,
  useGetSubscriptionsQuery
} from 'store/Subscription/SubscriptionApi';

function PricingPageForClient() {
  const toast = useToast();
  const navigate = useNavigate();

  // URL params
  const { action: plan, customerId } = useParams();
  const [searchParams] = useSearchParams();
  const billing = (searchParams.get("billing") || "monthly").toLowerCase(); // default monthly
  const priceIdFromUrl = searchParams.get("priceId") || null;

  // UI state
  const [showCheckout, setShowCheckout] = useState(false);
  const [addPaymentDetails, setAddPaymentDetails] = useState(false);
  const [allowed, setAllowed] = useState(null);
  const [hasError, setHasError] = useState(false);
  const [starterClientSecret, setStarterClientSecret] = useState(null);
  const [proClientSecret, setProClientSecret] = useState(null);
  const [addPaymentClientSecret, setAddPaymentClientSecret] = useState(null);

  // Loading overlay for “Get started” → EmbeddedCheckoutProvider
  const [isLoadingCheckout, setIsLoadingCheckout] = useState(false);

  const { data: subscriptionList } = useGetSubscriptionsQuery();

  // Derive the "current" subscription ID from the list (non-canceled)
  const listCurrent = useMemo(
    () =>
      (subscriptionList?.data?.length
        ? subscriptionList.data.find(sub => sub.status !== 'canceled') || null
        : null),
    [subscriptionList]
  );
  const subscriptionIdToFetch = listCurrent?._id || null;

  const { data: subscriptionById } = useGetSubscriptionByIdQuery(
    subscriptionIdToFetch ? subscriptionIdToFetch : skipToken
  );

  const currentSubscription = subscriptionById ?? null;
  const hasAnySubscription = Array.isArray(subscriptionList?.data) && subscriptionList.data.length > 0;

  const [createCheckoutSession] = useCreateCheckoutMutation();
  const [addPaymentMethod] = useAddPaymentMethodMutation();

  const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

  // Map plan + cycle -> Stripe price IDs 
  const PRICE_IDS = {
    Starter: {
      monthly: "price_1Ry5kIJu2L4goiIVfNzuPThJ", // From API
      yearly: "price_1Ry5jKJu2L4goiIVAHIZbzVk",  // From API 
    },
    Pro: {
      monthly: "price_1Ry5kIJu2L4goiIVfNzuPThJ", // From Pro monthly plan in API
      yearly: "price_1Ry5lAJu2L4goiIVrTIU8Djn",  // From API 
    },
  };
  

  // Decide which priceId to use (URL overrides map)
  const effectivePriceId =
    priceIdFromUrl ||
    PRICE_IDS?.[plan]?.[billing] ||
    null;

  // Gate access via localStorage flag
  useEffect(() => {
    const isAllowed = localStorage.getItem("allowPricingEntry");
    if (!isAllowed) {
      navigate('/not-found');
      setAllowed(false);
    } else {
      setAllowed(true);
    }
  }, [navigate]);

  // Clean up flag on unmount
  useEffect(() => {
    return () => {
      localStorage.removeItem("allowPricingEntry");
    };
  }, []);

  const handleError = (error) => {
    setIsLoadingCheckout(false);
    setHasError(true);
    toast({
      position: "top-right",
      title: "Something went wrong",
      description: error?.data?.message || error?.message || "Please try again.",
      status: "error",
      duration: 4000,
      isClosable: true,
    });
  };

  useEffect(() => {
    if (!allowed || !plan) return;

    const isPlanSelected = plan === 'Starter' || plan === 'Pro';

    // If plan is recognized but we don't have a price for the requested cycle, block.
    if (isPlanSelected && !effectivePriceId) {
      navigate('/not-found');
      return;
    }

    // Prevent access if user already on same plan or already active
    const currentPlanName = currentSubscription?.planRef?.name;
    const isSamePlan = currentPlanName === plan;
    if ((isPlanSelected && isSamePlan)) {
      navigate('/not-found');
      return;
    }

    // ——— Add payment method only (no plan change) ———
    if (customerId) {
      setIsLoadingCheckout(true);
      addPaymentMethod({ customerId })
        .unwrap()
        .then((response) => {
          const secret = response?.data?.client_secret;
          if (!secret) throw new Error("Client secret not returned");
          setAddPaymentClientSecret(secret);
          setShowCheckout(true);
          // We can keep the overlay until the embedded wrapper says “ready” (optional),
          // otherwise hide now since provider is about to render:
          setIsLoadingCheckout(false);
        })
        .catch(handleError);
      return;
    }

    // ——— Plan change / new checkout ———
    if (isPlanSelected) {
      setIsLoadingCheckout(true);
      createCheckoutSession({
        payload: {
          priceId: effectivePriceId,
          billingCycle: billing,
          name: plan,
          // force payment if any subscription exists
          skip: hasAnySubscription ? false : !addPaymentDetails,
        }
      })
        .unwrap()
        .then((response) => {
          const secret = response?.data?.client_secret;
          if (!secret) throw new Error("Client secret not returned");

          if (plan === 'Starter') setStarterClientSecret(secret);
          else setProClientSecret(secret);

          setShowCheckout(true);
          // Optional: keep spinner until wrapper signals onReady.
          // If not using onReady, hide now to avoid indefinite overlay:
          setIsLoadingCheckout(false);
        })
        .catch(handleError);
    }
  }, [
    allowed,
    plan,
    customerId,
    addPaymentDetails,
    currentSubscription?.planRef?.name,
    currentSubscription?.status,
    billing,
    effectivePriceId,
  ]);

  const closeCheckout = () => {
    setShowCheckout(false);
    setStarterClientSecret(null);
    setProClientSecret(null);
    setAddPaymentClientSecret(null);
    localStorage.removeItem("allowPricingEntry");
  };

  return (
    <SidebarWithHeader noPadding={true}>
      <Box>
      {isLoadingCheckout ? (
        <Flex
          direction="column"
          gap={2}
          alignItems="center"
          justifyContent="center"
          h="70vh"
        >
        <Spinner />
          <Text>Loading…</Text>
        </Flex>
              ) : 
        <Flex direction="row" align="start">
          {/* Left: Back Button */}
          <Box p={4} minW="80px">
            <Tooltip label="Back" fontSize="xs">
              <Box>
              <Icon
                as={TiArrowBack}
                boxSize={6}
                cursor="pointer"
                onClick={() => navigate(customerId ? '/subscription' : '/pricing')}
              />
                    </Box>
            </Tooltip>
          </Box>

          {/* Center: Main Content */}
          <Flex flex="1" justify="center" align="start">
            <Box w="100%" maxW="600px">
              {hasError ? (
                <OopsPage />
              ) : (
                <>
                  {(plan === 'Starter' || plan === 'Pro') && !hasAnySubscription && (
                    <Flex justifyContent="center" p={4}>
                      <Checkbox
                        colorScheme="brand"
                        isChecked={addPaymentDetails}
                        onChange={(e) => setAddPaymentDetails(e.target.checked)}
                      >
                        Add payment details
                      </Checkbox>
                    </Flex>
                  )}

                  {showCheckout && (
                    <>
                      {starterClientSecret && plan === 'Starter' && (
                        <EmbeddedCheckoutProvider
                          key={starterClientSecret}
                          stripe={stripePromise}
                          options={{ clientSecret: starterClientSecret }}
                        >
                          <EmbeddedCheckoutWrapper
                            clientSecret={starterClientSecret}
                            stripePromise={stripePromise}
                            onEmbeddedCheckoutComplete={closeCheckout}
                          />
                        </EmbeddedCheckoutProvider>
                      )}

                      {proClientSecret && plan === 'Pro' && (
                        <EmbeddedCheckoutProvider
                          key={proClientSecret}
                          stripe={stripePromise}
                          options={{ clientSecret: proClientSecret }}
                        >
                          <EmbeddedCheckoutWrapper
                            clientSecret={proClientSecret}
                            stripePromise={stripePromise}
                            onEmbeddedCheckoutComplete={closeCheckout}
                          />
                        </EmbeddedCheckoutProvider>
                      )}

                      {addPaymentClientSecret && customerId && (
                        <EmbeddedCheckoutProvider
                          key={addPaymentClientSecret}
                          stripe={stripePromise}
                          options={{ clientSecret: addPaymentClientSecret }}
                        >
                          <EmbeddedCheckoutWrapper
                            clientSecret={addPaymentClientSecret}
                            stripePromise={stripePromise}
                            onEmbeddedCheckoutComplete={closeCheckout}
                          />
                        </EmbeddedCheckoutProvider>
                      )}
                    </>
                  )}
                </>
              )}
            </Box>
          </Flex>
        </Flex>
      }
      </Box>
  </SidebarWithHeader>
  
  );
}

export default PricingPageForClient;
